import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  Folder,
  FolderOpen,
  MoreVert,
  CreateNewFolder,
} from '@mui/icons-material';
import useCategories from '../hooks/data/useCategories';
import { useCategoryContext } from '../contexts/CategoryContext';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useCategoryMutations } from '../hooks/data/useCategoryMutations';
import { useCacheStore } from '../hooks/data/useCacheStore';
import { CACHE_UPDATED_EVENT } from '../hooks/data/useCacheFirstQuery';
import { supabaseData } from '../utils/supabase-data-client';
import { ConfirmDialog } from './ConfirmDialog';
import { CategoryPicker } from './CategoryPicker';
import NoteTree from './NoteTree';
import { Category } from '../types/bookmarks';

/**
 * Computes all descendant category IDs for a given category.
 * Used to disable self + descendants in CategoryPicker to prevent circular moves.
 */
function getDescendantIds(
  categoryId: string,
  allCategories: Category[],
): Set<string> {
  const descendants = new Set<string>();
  const queue = [categoryId];
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    for (const cat of allCategories) {
      if (cat.parent_id === currentId) {
        descendants.add(cat.category_id);
        queue.push(cat.category_id);
      }
    }
  }
  return descendants;
}

interface TreeSidebarProps {
  open: boolean;
  onClose: () => void;
  onAddCategory?: () => void;
}

interface CategoryTreeNodeProps {
  category: Category;
  depth: number;
  onSelect: (category: Category) => void;
  selectedCategoryId: string | null;
  subfolderedCategoryId?: string | null;
  onSubfolderTriggered?: () => void;
  expandedAncestorIds?: Set<string>;
}

/**
 * Inner component that fetches and renders children for an expanded category node.
 * Separated so that useCategories only runs when the node is actually expanded.
 */
const CategoryChildren: React.FC<{
  parentId: string;
  depth: number;
  onSelect: (category: Category) => void;
  selectedCategoryId: string | null;
  subfolderedCategoryId?: string | null;
  onSubfolderTriggered?: () => void;
  expandedAncestorIds?: Set<string>;
}> = ({
  parentId,
  depth,
  onSelect,
  selectedCategoryId,
  subfolderedCategoryId,
  onSubfolderTriggered,
  expandedAncestorIds,
}) => {
  const { categories: children, loading } = useCategories(parentId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <List component="div" disablePadding>
      {children.map((child) => (
        <CategoryTreeNode
          key={child.category_id}
          category={child}
          depth={depth + 1}
          onSelect={onSelect}
          selectedCategoryId={selectedCategoryId}
          subfolderedCategoryId={subfolderedCategoryId}
          onSubfolderTriggered={onSubfolderTriggered}
          expandedAncestorIds={expandedAncestorIds}
        />
      ))}
    </List>
  );
};

/**
 * Recursive tree node component for a single category.
 * Lazy-loads children on expand via CategoryChildren sub-component.
 * Supports context menu actions: Add Subfolder, Rename, Delete, Move.
 */
const CategoryTreeNode: React.FC<CategoryTreeNodeProps> = ({
  category,
  depth,
  onSelect,
  selectedCategoryId,
  subfolderedCategoryId,
  onSubfolderTriggered,
  expandedAncestorIds,
}) => {
  const shouldAutoExpand =
    expandedAncestorIds?.has(category.category_id) ?? false;
  const [expanded, setExpanded] = useState(shouldAutoExpand);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Add subfolder dialog state
  const [showSubfolderDialog, setShowSubfolderDialog] = useState(false);
  const [subfolderName, setSubfolderName] = useState('');
  const [subfolderError, setSubfolderError] = useState('');
  const [subfolderLoading, setSubfolderLoading] = useState(false);

  // Rename dialog state
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);

  // Move picker state
  const [showMovePicker, setShowMovePicker] = useState(false);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [infoDialog, setInfoDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({
    open: false,
    title: '',
    message: '',
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'error',
  });

  // Hooks
  const { user } = useWebAuthn();
  const { isOnline } = useOnlineStatus();
  const { addCategory, renameCategory, deleteCategory, moveCategory } =
    useCategoryMutations();
  const cacheStore = useCacheStore();
  const { selectedCategory, selectCategory } = useCategoryContext();

  const isSelected = selectedCategoryId === category.category_id;

  // Check if this category has children (used to show/hide expand chevron)
  // Re-evaluates when cache is updated (e.g., after adding/deleting a subfolder)
  const [hasChildren, setHasChildren] = useState(() => {
    const allCategories = cacheStore.getCategories() || [];
    return allCategories.some((c) => c.parent_id === category.category_id);
  });

  useEffect(() => {
    const recheck = () => {
      const allCategories = cacheStore.getCategories() || [];
      setHasChildren(
        allCategories.some((c) => c.parent_id === category.category_id),
      );
    };
    window.addEventListener(CACHE_UPDATED_EVENT, recheck);
    return () => window.removeEventListener(CACHE_UPDATED_EVENT, recheck);
  }, [cacheStore, category.category_id]);

  // Respond to external subfolder add trigger
  useEffect(() => {
    if (subfolderedCategoryId === category.category_id) {
      setSubfolderName('');
      setSubfolderError('');
      setShowSubfolderDialog(true);
      onSubfolderTriggered?.();
    }
  }, [subfolderedCategoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleSelect = () => {
    onSelect(category);
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // === ADD SUBFOLDER ===
  const handleAddSubfolder = () => {
    handleMenuClose();
    setSubfolderName('');
    setSubfolderError('');
    setShowSubfolderDialog(true);
  };

  const handleSubfolderSave = async () => {
    const trimmed = subfolderName.trim();
    if (!trimmed) {
      setSubfolderError('Category name is required.');
      return;
    }
    if (trimmed.length > 100) {
      setSubfolderError('Category name must be 100 characters or fewer.');
      return;
    }

    setSubfolderLoading(true);
    setSubfolderError('');

    const result = await addCategory(trimmed, category.category_id);

    setSubfolderLoading(false);

    if (result.success) {
      setShowSubfolderDialog(false);
      setSubfolderName('');
      setSubfolderError('');
      setExpanded(true);
    } else if (result.constraintViolation) {
      setSubfolderError(
        result.error ||
          'A category with that name already exists at this level.',
      );
    } else {
      setSubfolderError(result.error || 'Failed to create category.');
    }
  };

  const handleSubfolderCancel = () => {
    setShowSubfolderDialog(false);
    setSubfolderName('');
    setSubfolderError('');
  };

  // === RENAME ===
  const handleRename = () => {
    handleMenuClose();
    setRenameName(category.category_name);
    setRenameError('');
    setShowRenameDialog(true);
  };

  const handleRenameSave = async () => {
    const trimmed = renameName.trim();
    if (!trimmed) {
      setRenameError('Category name is required.');
      return;
    }
    if (trimmed.length > 100) {
      setRenameError('Category name must be 100 characters or fewer.');
      return;
    }

    setRenameLoading(true);
    setRenameError('');

    const result = await renameCategory(category.category_id, trimmed);

    setRenameLoading(false);

    if (result.success) {
      setShowRenameDialog(false);
      setRenameName('');
      setRenameError('');
    } else if (result.constraintViolation) {
      setRenameError(
        result.error ||
          'A category with that name already exists at this level.',
      );
    } else {
      setRenameError(result.error || 'Failed to rename category.');
    }
  };

  const handleRenameCancel = () => {
    setShowRenameDialog(false);
    setRenameName('');
    setRenameError('');
  };

  // === DELETE ===
  const handleDelete = () => {
    handleMenuClose();

    // Check for child categories
    const allCategories = cacheStore.getCategories() || [];
    const children = allCategories.filter(
      (c) => c.parent_id === category.category_id,
    );

    if (children.length > 0) {
      // Has children: show info dialog (cannot delete)
      setInfoDialog({
        open: true,
        title: 'Cannot Delete',
        message:
          'This category contains subcategories. Remove or move them first.',
      });
      return;
    }

    // Check for bookmarks in this category
    const allBookmarks = cacheStore.getBookmarks() || [];
    const categoryBookmarks = allBookmarks.filter(
      (b) => b.category_id === category.category_id,
    );

    if (categoryBookmarks.length > 0) {
      // Has bookmarks: show warning confirm dialog
      setConfirmDialog({
        open: true,
        title: 'Delete Category',
        message: `Bookmarks in "${category.category_name}" will be moved to Uncategorized.`,
        onConfirm: () => performDeleteWithBookmarks(),
      });
    } else {
      // No children, no bookmarks: confirm before deleting
      setConfirmDialog({
        open: true,
        title: 'Delete Category',
        message: `Are you sure you want to delete "${category.category_name}"?`,
        onConfirm: () => performDeleteDirect(),
      });
    }
  };

  const performDeleteWithBookmarks = async () => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));

    try {
      // Find or create Uncategorized category
      const allCategories = cacheStore.getCategories() || [];
      let uncategorized = allCategories.find(
        (c) => c.category_name === 'Uncategorized' && c.parent_id === null,
      );

      if (!uncategorized) {
        // Create Uncategorized category
        const createResult = await addCategory('Uncategorized', null);
        if (!createResult.success) {
          setSnackbar({
            open: true,
            message:
              createResult.error || 'Failed to create Uncategorized category.',
            severity: 'error',
          });
          return;
        }
        // Re-read from cache after creation
        const updatedCategories = cacheStore.getCategories() || [];
        uncategorized = updatedCategories.find(
          (c) => c.category_name === 'Uncategorized' && c.parent_id === null,
        );
        if (!uncategorized) {
          setSnackbar({
            open: true,
            message: 'Failed to find Uncategorized category after creation.',
            severity: 'error',
          });
          return;
        }
      }

      // Reassign bookmarks to Uncategorized
      const { error: reassignError } = await supabaseData
        .from('bookmarks')
        .update({ category_id: uncategorized.category_id })
        .eq('category_id', category.category_id);

      if (reassignError) {
        setSnackbar({
          open: true,
          message: reassignError.message || 'Failed to reassign bookmarks.',
          severity: 'error',
        });
        return;
      }

      // Delete the category
      const deleteResult = await deleteCategory(category.category_id, {
        bookmarksReassigned: true,
      });
      if (!deleteResult.success) {
        setSnackbar({
          open: true,
          message: deleteResult.error || 'Failed to delete category.',
          severity: 'error',
        });
        return;
      }

      // Navigate away if this was selected
      navigateAfterDelete();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred.';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const performDeleteDirect = async () => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
    const result = await deleteCategory(category.category_id);
    if (!result.success) {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to delete category.',
        severity: 'error',
      });
      return;
    }
    navigateAfterDelete();
  };

  const navigateAfterDelete = () => {
    if (selectedCategory?.category_id === category.category_id) {
      // Navigate to parent or root
      if (category.parent_id) {
        const allCategories = cacheStore.getCategories() || [];
        const parent = allCategories.find(
          (c) => c.category_id === category.parent_id,
        );
        selectCategory(parent || null);
      } else {
        selectCategory(null);
      }
    }
  };

  // === MOVE ===
  const handleMove = () => {
    handleMenuClose();
    setShowMovePicker(true);
  };

  const disabledMoveIds = useMemo(() => {
    const allCategories = cacheStore.getCategories() || [];
    const descendants = getDescendantIds(category.category_id, allCategories);
    descendants.add(category.category_id);
    return descendants;
  }, [category.category_id, cacheStore]);

  const handleMoveSelect = async (newParentId: string | null) => {
    const result = await moveCategory(category.category_id, newParentId);
    if (result.success) {
      setShowMovePicker(false);
    } else if (result.constraintViolation) {
      setSnackbar({
        open: true,
        message:
          result.error ||
          'A category with that name already exists at the destination level.',
        severity: 'error',
      });
      setShowMovePicker(false);
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to move category.',
        severity: 'error',
      });
      setShowMovePicker(false);
    }
  };

  const handleMoveCancel = () => {
    setShowMovePicker(false);
  };

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={handleSelect}
        sx={{
          pl: 2 + depth * 2,
          minHeight: 44,
          minWidth: 44,
          '& .category-menu-trigger': {
            opacity: { xs: 1, sm: 0 },
            transition: 'opacity 0.2s',
          },
          '&:hover .category-menu-trigger': {
            opacity: 1,
          },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={handleToggleExpand}
            sx={{ minHeight: 44, minWidth: 44, mr: 0.5 }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ExpandMore /> : <ChevronRight />}
          </IconButton>
        ) : (
          <Box sx={{ width: 44, minHeight: 44, mr: 0.5 }} />
        )}
        <ListItemIcon sx={{ minWidth: 32 }}>
          {expanded && hasChildren ? (
            <FolderOpen fontSize="small" />
          ) : (
            <Folder fontSize="small" />
          )}
        </ListItemIcon>
        <Tooltip
          title={category.category_name}
          enterDelay={500}
          placement="right"
        >
          <ListItemText
            primary={category.category_name}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </Tooltip>
        {user && (
          <IconButton
            size="small"
            className="category-menu-trigger"
            onClick={handleMenuOpen}
            sx={{ minHeight: 44, minWidth: 44 }}
            aria-label={`Actions for ${category.category_name}`}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        )}
      </ListItemButton>

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRename} disabled={!isOnline}>
          Rename
        </MenuItem>
        <MenuItem onClick={handleAddSubfolder} disabled={!isOnline}>
          Add Subfolder
        </MenuItem>
        <MenuItem onClick={handleDelete} disabled={!isOnline}>
          Delete
        </MenuItem>
        <MenuItem onClick={handleMove} disabled={!isOnline}>
          Move
        </MenuItem>
      </Menu>

      {/* Children - only rendered when expanded (lazy-load) */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CategoryChildren
          parentId={category.category_id}
          depth={depth}
          onSelect={onSelect}
          selectedCategoryId={selectedCategoryId}
          subfolderedCategoryId={subfolderedCategoryId}
          onSubfolderTriggered={onSubfolderTriggered}
          expandedAncestorIds={expandedAncestorIds}
        />
      </Collapse>

      {/* Confirm Dialog (delete with bookmarks) */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      />

      {/* Info Dialog (cannot delete - has children) */}
      <Dialog
        open={infoDialog.open}
        onClose={() => setInfoDialog((prev) => ({ ...prev, open: false }))}
      >
        <DialogTitle>{infoDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{infoDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInfoDialog((prev) => ({ ...prev, open: false }))}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Picker (Move) */}
      <CategoryPicker
        open={showMovePicker}
        title="Move Category"
        disabledIds={disabledMoveIds}
        currentLocationId={category.parent_id}
        onSelect={handleMoveSelect}
        onCancel={handleMoveCancel}
      />

      {/* Rename Dialog */}
      <Dialog
        open={showRenameDialog}
        onClose={handleRenameCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !renameLoading) {
                e.preventDefault();
                handleRenameSave();
              }
            }}
            inputProps={{ maxLength: 100 }}
            error={!!renameError}
            helperText={renameError}
            disabled={renameLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameCancel} disabled={renameLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleRenameSave}
            variant="contained"
            disabled={renameLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Subfolder Dialog */}
      <Dialog
        open={showSubfolderDialog}
        onClose={handleSubfolderCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{`Add Subfolder to "${category.category_name}"`}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={subfolderName}
            onChange={(e) => setSubfolderName(e.target.value)}
            inputProps={{ maxLength: 100 }}
            error={!!subfolderError}
            helperText={subfolderError}
            disabled={subfolderLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubfolderCancel} disabled={subfolderLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubfolderSave}
            variant="contained"
            disabled={subfolderLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for errors */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

/**
 * Renders the root-level categories under the "Bookmarks" node.
 * Separated to only invoke useCategories(null) when Bookmarks is expanded.
 */
const RootCategoryList: React.FC<{
  onSelect: (category: Category) => void;
  selectedCategoryId: string | null;
  subfolderedCategoryId?: string | null;
  onSubfolderTriggered?: () => void;
  expandedAncestorIds?: Set<string>;
}> = ({
  onSelect,
  selectedCategoryId,
  subfolderedCategoryId,
  onSubfolderTriggered,
  expandedAncestorIds,
}) => {
  const { categories: rootCategories, loading } = useCategories(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <List component="div" disablePadding>
      {rootCategories.map((category) => (
        <CategoryTreeNode
          key={category.category_id}
          category={category}
          depth={1}
          onSelect={onSelect}
          selectedCategoryId={selectedCategoryId}
          subfolderedCategoryId={subfolderedCategoryId}
          onSubfolderTriggered={onSubfolderTriggered}
          expandedAncestorIds={expandedAncestorIds}
        />
      ))}
    </List>
  );
};

/**
 * TreeSidebar displays a collapsible category tree with two root nodes:
 * "Bookmarks" (expandable with lazy-loaded categories) and "Notes" (coming soon placeholder).
 *
 * On mobile, accepts `open` and `onClose` props for drawer control.
 * Closes drawer on category select.
 */
const TreeSidebar: React.FC<TreeSidebarProps> = ({
  open,
  onClose,
  onAddCategory,
}) => {
  const { selectedCategory, ancestors, selectCategory } = useCategoryContext();
  const [bookmarksExpanded, setBookmarksExpanded] = useState(
    !!selectedCategory,
  );

  // Subfolder add state (triggered from CategoryTreeNode context menu)
  const [subfolderedCategoryId, setSubfolderedCategoryId] = useState<
    string | null
  >(null);

  // Root node context menu
  const [rootMenuAnchor, setRootMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const { user } = useWebAuthn();
  const { isOnline } = useOnlineStatus();

  // Compute the set of ancestor IDs that should be auto-expanded
  const expandedAncestorIds = useMemo(() => {
    const ids = new Set<string>();
    for (const ancestor of ancestors) {
      ids.add(ancestor.category_id);
    }
    return ids;
  }, [ancestors]);

  // Auto-expand Bookmarks root when selectedCategory is restored on mount
  useEffect(() => {
    if (selectedCategory) {
      setBookmarksExpanded(true);
    }
  }, [selectedCategory]);

  const handleSubfolderTriggered = useCallback(() => {
    setSubfolderedCategoryId(null);
  }, []);

  const handleRootMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setRootMenuAnchor(e.currentTarget);
  };

  const handleRootMenuClose = () => {
    setRootMenuAnchor(null);
  };

  const handleRootAddCategory = () => {
    handleRootMenuClose();
    onAddCategory?.();
  };

  const handleBookmarksToggle = () => {
    setBookmarksExpanded((prev) => !prev);
  };

  const handleCategorySelect = useCallback(
    (category: Category) => {
      selectCategory(category);
      onClose();
    },
    [selectCategory, onClose],
  );

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ px: 2, pt: 2, pb: 1, color: 'text.secondary', fontWeight: 600 }}
      >
        Navigation
      </Typography>

      <List component="nav" dense>
        {/* Bookmarks root node */}
        <ListItemButton
          onClick={handleBookmarksToggle}
          sx={{
            minHeight: 44,
            minWidth: 44,
            '& .root-menu-trigger': {
              opacity: { xs: 1, sm: 0 },
              transition: 'opacity 0.2s',
            },
            '&:hover .root-menu-trigger': {
              opacity: 1,
            },
          }}
        >
          <IconButton
            size="small"
            sx={{ minHeight: 44, minWidth: 44, mr: 0.5 }}
            aria-label={
              bookmarksExpanded ? 'Collapse Bookmarks' : 'Expand Bookmarks'
            }
            tabIndex={-1}
          >
            {bookmarksExpanded ? <ExpandMore /> : <ChevronRight />}
          </IconButton>
          <ListItemIcon sx={{ minWidth: 32 }}>
            {bookmarksExpanded ? <FolderOpen /> : <Folder />}
          </ListItemIcon>
          <ListItemText
            primary="Bookmarks"
            primaryTypographyProps={{ fontWeight: 600 }}
          />
          {user && (
            <IconButton
              size="small"
              className="root-menu-trigger"
              onClick={handleRootMenuOpen}
              sx={{ minHeight: 44, minWidth: 44 }}
              aria-label="Bookmarks actions"
            >
              <MoreVert fontSize="small" />
            </IconButton>
          )}
        </ListItemButton>

        {/* Root node context menu */}
        <Menu
          anchorEl={rootMenuAnchor}
          open={Boolean(rootMenuAnchor)}
          onClose={handleRootMenuClose}
        >
          <MenuItem onClick={handleRootAddCategory} disabled={!isOnline}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CreateNewFolder fontSize="small" />
            </ListItemIcon>
            Add Category
          </MenuItem>
        </Menu>

        <Collapse in={bookmarksExpanded} timeout="auto" unmountOnExit>
          <RootCategoryList
            onSelect={handleCategorySelect}
            selectedCategoryId={selectedCategory?.category_id ?? null}
            subfolderedCategoryId={subfolderedCategoryId}
            onSubfolderTriggered={handleSubfolderTriggered}
            expandedAncestorIds={expandedAncestorIds}
          />
        </Collapse>

        {/* Notes root node */}
        <NoteTree onClose={onClose} />
      </List>
    </Box>
  );
};

export default TreeSidebar;
